import { env } from '../config/env';

interface ClarityFn {
  (...args: unknown[]): void;
  q?: unknown[];
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: ClarityFn;
  }
}

// Element ids double as the idempotency guard: if the tag is already in the DOM (e.g. a second
// initAnalytics() call from a HMR reload or an accidental double-mount), skip re-inserting it
// rather than appending a duplicate <script>.
const GA_SCRIPT_ID = 'ga4-gtag-script';
const CLARITY_SCRIPT_ID = 'ms-clarity-script';

function loadGoogleAnalytics(measurementId: string): void {
  if (document.getElementById(GA_SCRIPT_ID)) return;

  window.dataLayer = window.dataLayer || [];
  // Exposed on window (matching Google's own snippet) so other parts of the app can emit
  // custom events via window.gtag(...) later, consistent with vendor expectations.
  //
  // Must push the `arguments` object, not a rest-param array: gtag.js's own dataLayer.push
  // override (installed once the real script loads) distinguishes gtag() commands from plain
  // pushed objects by their shape, and a real Array doesn't match what it expects there. With
  // a rest-param array, the container still bootstraps (script loads, registerDestination
  // fires) but never actually sends a hit - confirmed via a standalone repro comparing this
  // against Google's literal install snippet (COMP-2032).
  window.gtag ??= function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);

  const script = document.createElement('script');
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

function loadMsClarity(projectId: string): void {
  if (document.getElementById(CLARITY_SCRIPT_ID)) return;

  if (!window.clarity) {
    const clarity: ClarityFn = (...args: unknown[]) => {
      const queue = clarity.q || [];
      clarity.q = queue;
      queue.push(args);
    };
    window.clarity = clarity;
  }

  const script = document.createElement('script');
  script.id = CLARITY_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${projectId}`;
  document.head.appendChild(script);
}

let initialized = false;

/**
 * Bootstraps GA4 (platform analytics) and MS Clarity (session recording), each independently
 * no-op when its ID is blank - so environments that omit the ConfigMap key (pre-prod) stay
 * tracking-free without any special-casing here. Idempotent: safe to call more than once.
 */
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  if (env.gaMeasurementId) {
    loadGoogleAnalytics(env.gaMeasurementId);
  }
  if (env.clarityProjectId) {
    loadMsClarity(env.clarityProjectId);
  }
}
